import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

export type TAuthMode = 'login' | 'register';

/**
 * Статус сессии (паритет с нативным AuthService):
 * - `unknown` — ещё не определён (до bootstrap);
 * - `guest` — гость (раньше не авторизовался) — ходит свободно до закрытого ресурса;
 * - `returning` — вернувшийся (авторизовался ранее), но сессии нет — должен залогиниться;
 * - `authenticated` — активная сессия.
 */
export type TAuthStatus = 'unknown' | 'guest' | 'returning' | 'authenticated';

/** Профиль текущего пользователя (из ответа login/register). */
export interface ISessionUser {
  id: string;
  phone?: string;
  email?: string;
  name?: string;
}

interface IAuthState {
  /** Активна ли сессия (status === 'authenticated'). Держим отдельно для удобных селекторов. */
  isAuthorized: boolean;
  status: TAuthStatus;
  /** Профиль пользователя; null — нет данных (гость или ещё не загружен). */
  user: ISessionUser | null;
  /** Состояние оверлея авторизации (open/mode). Промис openAuth живёт вне стора. */
  overlay: {
    open: boolean;
    mode: TAuthMode;
  };
}

const initialState: IAuthState = {
  isAuthorized: false,
  status: 'unknown',
  user: null,
  overlay: {
    open: false,
    mode: 'login'
  }
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setIsAuthorized: (state, action: PayloadAction<boolean>) => {
      state.isAuthorized = action.payload;
      state.status = action.payload ? 'authenticated' : 'guest';
    },
    setAuthStatus: (state, action: PayloadAction<TAuthStatus>) => {
      state.status = action.payload;
      state.isAuthorized = action.payload === 'authenticated';
    },
    setUser: (state, action: PayloadAction<ISessionUser | null>) => {
      state.user = action.payload;
    },
    openAuthOverlay: (state, action: PayloadAction<TAuthMode | undefined>) => {
      state.overlay.open = true;
      state.overlay.mode = action.payload ?? 'login';
    },
    closeAuthOverlay: (state) => {
      state.overlay.open = false;
    },
    setAuthMode: (state, action: PayloadAction<TAuthMode>) => {
      state.overlay.mode = action.payload;
    }
  }
});

export const {
  setIsAuthorized,
  setAuthStatus,
  setUser,
  openAuthOverlay,
  closeAuthOverlay,
  setAuthMode
} = authSlice.actions;
export default authSlice;
